/* eslint-disable react/prop-types, react/display-name */
import { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import CreatableSelect from 'react-select/creatable'

import { components } from 'react-select'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { getQuestionId, getQuestionIcon, removeLogicFromText } from '@aytm/app/stats/page/components/lib/utils'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionCrosstab.module.scss'
import * as classNames from 'classnames'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const { MenuList, MultiValue, Option, ClearIndicator, IndicatorsContainer } = components

const levelsOptions = (PageGlobals.CONFIDENCE_LEVELS || []).map(level => {
  return { key: level[0], text: level[1] }
})

const getBannerKey = question => {
  const id = getQuestionId(question)

  const type = question.variant || question.type
  const key = `${id}${'psc' == type ? 'psc' : ''}`

  return 'dlx' == question.type
    ? Object.hasOwn(question, 'batches')
      ? `${key}:${question.batches.map(b => `sq${b.position}`).join('-')}`
      : `${key}:${question.answerVariants.map(av => `a${av.position}`).join('-')}`
    : key
}

const searchInputStyles = {
  option: (provided, state) => {
    return { ...provided, isDisabled: state.data.disabled }
  },
  multiValue: (provided, state) => {
    return { ...provided, backgroundColor: state.styles.backgroundColor }
  }
}

// The popup's ear sits at its right edge, so pushing the popup in by half the button less half the
// ear lands it on the button's centre -- whatever the label happens to say. Measured, not pinned:
// the button's width moves with its label and with the wheel standing beside it.
const EAR_WIDTH = 20

const QuestionCrosstab = ({
  popupDirection = 'topright',
  requestFailed,
  isThinking,
  getCrosstabData,
  onExplore,
  onCrosstabChange,
  onDismissFailure
}) => {
  const { survey, report } = getCrosstabData()

  const [bannersValue, setBannersValue] = useState([])

  const [confidenceLevel, setConfidenceLevel] = useState(0.95)
  const [significanceLevel, setSignificanceLevel] = useState(0.9)

  const confidenceLevelRef = useRef(null)
  confidenceLevelRef.current = confidenceLevel
  const significanceLevelRef = useRef(null)
  significanceLevelRef.current = significanceLevel

  const traits = useMemo(
    () =>
      Array.isArray(survey.options.crosstabTraits)
        ? convertTraits(survey).reduce((acc, cur) => {
            if (!survey.options.crosstabTraits.includes(cur.id)) return acc
            if ('country' == cur.id && 'WRD' != survey.country.id) return acc
            if (cur.missing || !Object.hasOwn(survey.refsList || {}, cur.id.toUpperCase())) return acc
            acc.push(cur)
            return acc
          }, [])
        : [],
    [survey]
  )

  const variables = useMemo(
    () =>
      Object.keys(survey.customVariables || {}).reduce((acc, cur) => {
        if (
          !(true == survey.customVariables[cur].long || 0 < survey.customVariables[cur].values.length) ||
          !/conjoint_cluster\d+/.test(cur)
        )
          return acc
        acc.push(Object.assign({ key: cur }, survey.customVariables[cur]))
        return acc
      }, []),
    [survey.customVariables]
  )

  const questions = useMemo(() => {
    const reg = new RegExp('^xq(\\d+)(?:[_](\\d+))?$', 'i')
    const xquestions = Object.hasOwn(survey.scripts || {}, 'questions')
      ? Object.values(report.current.virtual)
          .filter(q => reg.test(q.id))
          .reduce((acc, cur) => {
            const q = Object.values(survey.scripts.questions).find(
              q => q.id.startsWith(`${cur.id}_`) && q.blocks.some(b => 'chart' == b.type)
            )
            if (q) {
              const chart = q.blocks.find(b => 'chart' == b.type)
              acc.push(
                Object.assign({}, chart, {
                  id: cur.id,
                  key: q.id,
                  text: cur.text,
                  type: ('horizontal-bars' == chart.subtype && 'rb') || chart.subtype,
                  title: q.text,
                  position: 0
                })
              )
            }
            return acc
          }, [])
      : []

    return survey.questions
      .filter(q => report.current.schema.includes(`q${q.position}`))
      .concat(Object.values(report.current.virtual))
      .reduce((acc, cur) => {
        if ('dlx' == cur.type && 'gap-analysis' == cur.script) return acc

        const q = 'dlx' == cur.type ? xquestions.find(xq => xq.id == cur.id) : cur
        if (!q || 'none' == q.analysis) return acc

        const type = q.variant || q.type
        if (!PageGlobals.ALLOW_QUESTION_TYPES.includes(type)) return acc

        if (
          ['rb', 'cb', 'scx', 'sft'].includes(type) &&
          (!q.answerVariants?.length || 1000 <= (q.answerVariants.length * (q.answerVariants.length - 1)) / 2)
        )
          return acc
        if (
          ['mx', 'se', 'sr', 'mp'].includes(type) &&
          (!q.batches?.length ||
            1000 <=
              (q.batches.length * q.batches[0].answerVariants.length * (q.batches[0].answerVariants.length - 1)) / 2)
        )
          return acc
        if (
          ['qa', 'bpm'].includes(type) &&
          (!q.questionVariants?.length ||
            !q.batches?.length ||
            1000 <= (q.questionVariants.length * q.batches.length * (q.batches.length - 1)) / 2)
        )
          return acc
        if (
          ['ro', 'aro'].includes(type) &&
          (!q.batches?.length || 1000 <= (q.batches.length * q.batches.length * (q.batches.length - 1)) / 2)
        )
          return acc
        if (['psc'].includes(type) && (!q.batches?.length || 1000 <= 3 * q.batches.length)) return acc

        acc.push(q)
        return acc
      }, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, report.current.schema, report.current.virtual])

  const bannersOptions = useMemo(
    () =>
      traits
        .reduce(
          (acc, trait) =>
            bannersValue.some(banner => banner.key == trait.id)
              ? acc
              : acc.concat([{ key: trait.id, label: trait.title, value: trait, type: 'trait' }]),
          []
        )
        .concat(
          variables.reduce(
            (acc, variable) =>
              bannersValue.some(banner => banner.key == variable.key)
                ? acc
                : acc.concat([{ key: variable.key, label: variable.key, value: variable, type: 'variable' }]),
            []
          )
        )
        .concat(
          questions.reduce((acc, question) => {
            const id = getQuestionId(question)
            const key = getBannerKey(question)

            const text = `${id.toUpperCase()}: ${removeLogicFromText(question.text)}`

            const icon = getQuestionIcon(question)

            return bannersValue.some(banner => banner.key == key)
              ? acc
              : acc.concat([{ key, label: text, icon, value: question, type: 'question' }])
          }, [])
        ),
    [traits, variables, questions, bannersValue]
  )

  const overLimit = useMemo(() => {
    const numberOfConnections = bannersValue.reduce((amount, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        return amount + (banner.value.values?.length || 0)
      }

      if ('question' == banner.type) {
        const question = banner.value

        const type = question.variant || question.type

        if (['rb', 'cb', 'scx', 'sft'].includes(type))
          return amount + (question.answerVariants.length * (question.answerVariants.length - 1)) / 2
        if (['mx', 'se', 'sr', 'mp'].includes(type))
          return (
            amount +
            (question.batches.length *
              question.batches[0].answerVariants.length *
              (question.batches[0].answerVariants.length - 1)) /
              2
          )
        if (['qa', 'bpm'].includes(type))
          return (
            amount + (question.questionVariants.length * question.batches.length * (question.batches.length - 1)) / 2
          )
        if (['ro', 'aro'].includes(type))
          return amount + (question.batches.length * question.batches.length * (question.batches.length - 1)) / 2
        if (['psc'].includes(type)) return amount + 3 * question.batches.length
      }

      return amount
    }, 0)

    return 1000 <= numberOfConnections
  }, [bannersValue])

  const crossByLogic = useMemo(() => {
    return bannersValue.reduce((acc, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        acc.push(banner.key)
      }

      if ('question' == banner.type) {
        const question = banner.value

        const key =
          0 < question.position
            ? `${(question.pre_type && 'PQ') || 'Q'}${question.visual_position}`
            : question.id.toUpperCase()
        const type = question.variant || question.type

        if (Object.hasOwn(question, 'questionVariants') && Object.hasOwn(question, 'batches')) {
          for (const qv of question.questionVariants) {
            for (const b of question.batches) {
              acc.push(`${key}E${qv.position}SQ${b.position}`)
            }
          }
        } else if (Object.hasOwn(question, 'batches')) {
          for (const b of question.batches) {
            acc.push(`${key}${['ro', 'aro'].includes(type) ? 'A' : 'SQ'}${b.position}`)
          }
        } else acc.push(`${key}`)
      }

      return acc
    }, [])
  }, [bannersValue])

  const searchInputComponents = useMemo(() => {
    return {
      DropdownIndicator: null,
      MenuList: ({ children, ...props }) =>
        children.length ? <MenuList {...props}>{children}</MenuList> : <MenuList className="is-hidden" {...props} />,
      MultiValue: ({ children, data, ...props }) => {
        const backgroundColor = 'question' == data.type ? '#A8EBEC' : '#A9DDF3'

        return (
          <MultiValue data={data} styles={{ backgroundColor }} {...props}>
            {'question' == data.type ? (
              <span>{data.key.toUpperCase()}</span>
            ) : (
              <span dangerouslySetInnerHTML={{ __html: children }} />
            )}
          </MultiValue>
        )
      },
      Option: ({ children, data, ...props }) => (
        <Option data={data} {...props}>
          <span dangerouslySetInnerHTML={{ __html: children }} />
        </Option>
      ),
      NoOptionsMessage: () => null,
      IndicatorsContainer: props => <IndicatorsContainer {...props}>{props?.children}</IndicatorsContainer>,
      ClearIndicator: props => (props?.hasValue ? <ClearIndicator {...props} /> : null)
    }
  }, [])

  const onChange = (newValue, actionMeta) => {
    if ('clear' == actionMeta.action) {
      setBannersValue([])
      return
    }

    switch (actionMeta.action) {
      case 'pop-value':
      case 'remove-value':
      case 'select-option': {
        setBannersValue(newValue || [])
        break
      }
      case 'create-option':
      default: {
        break
      }
    }
  }

  useEffect(() => setSignificanceLevel(Math.min(significanceLevelRef.current, confidenceLevel)), [confidenceLevel])
  useEffect(() => setConfidenceLevel(Math.max(confidenceLevelRef.current, significanceLevel)), [significanceLevel])

  useEffect(
    () => onCrosstabChange({ confidenceLevel, significanceLevel, crossByLogic, overLimit }),
    [confidenceLevel, significanceLevel, crossByLogic, overLimit, onCrosstabChange]
  )

  const exploreRef = useRef(null)
  const [earOffset, setEarOffset] = useState(0)

  useLayoutEffect(() => {
    const width = exploreRef.current?.getBoundingClientRect().width

    if (width) setEarOffset(Math.max(0, Math.round(width / 2 - EAR_WIDTH / 2)))
  }, [isThinking, requestFailed])

  const renderExploreButton = () => (
    <Button
      data-testid="stat-skipper-explore"
      className="p-button-blue p-button-rounded"
      label="Explore"
      // the wheel stands on the button at rest and turns in place while Skipper thinks, rather than
      // arriving only once it has started -- and it is the same wheel the head mark carries. It wears
      // the family's own icon classes so it takes the leading-icon spacing the theme already states.
      icon={<i className="icon-aytmfd-skipper p-button-icon p-button-icon-left" />}
      loadingIcon={
        <i className={`icon-aytmfd-skipper p-button-icon p-button-icon-left ${style['explore-thinking']}`} />
      }
      loading={isThinking}
      disabled={isThinking || overLimit}
      onClick={onExplore}
    />
  )

  return (
    <div className={style.component}>
      <div className={style.label}>
        Confidence
        <br />
        Levels
      </div>

      <div className={`${style.level} p-fluid`}>
        <Dropdown
          className="p-dropdown-rounded p-dropdown-light-blue"
          aria-label="Primary confidence level"
          ariaLabel="Primary confidence level"
          value={confidenceLevel}
          options={levelsOptions}
          optionValue="key"
          optionLabel="text"
          fixedDropdownDir="bottom"
          filter={false}
          onChange={event => setConfidenceLevel(event.value)}
        />
      </div>
      <div className={`${style.level} p-fluid`}>
        <Dropdown
          className="p-dropdown-rounded p-dropdown-light-blue"
          aria-label="Secondary confidence level"
          ariaLabel="Secondary confidence level"
          value={significanceLevel}
          options={levelsOptions}
          optionValue="key"
          optionLabel="text"
          fixedDropdownDir="bottom"
          filter={false}
          onChange={event => setSignificanceLevel(event.value)}
        />
      </div>

      <div className={style.label}>
        Explore
        <br />
        against
      </div>

      <div className="p-fluid p-col">
        <CreatableSelect
          className={classNames('search-input', { 'react-select-error': overLimit })}
          classNamePrefix="search-input"
          components={searchInputComponents}
          styles={searchInputStyles}
          placeholder="add banners..."
          options={bannersOptions}
          value={bannersValue}
          createOptionPosition="first"
          noResultsText={null}
          openMenuOnClick
          openMenuOnFocus={false}
          cacheOptions
          isMulti
          isClearable
          isSearchable
          hideSelectedOptions
          isValidNewOption={() => false}
          onChange={onChange}
          getOptionValue={option => option.key}
          formatCreateLabel={() => null}
        />
      </div>

      {onExplore ? (
        <div ref={exploreRef} className={style.explore}>
          {requestFailed ? (
            <Popup
              className="rfd-ui-popup-warning"
              targetClassName={style['explore-popupTarget']}
              width="400px"
              trigger="none"
              direction={popupDirection}
              popupOffset={earOffset}
              show
              hideOtherPopupsOnShow={false}
              content={() => (
                <div className={style['explore-popupContent']} {...pressable(onDismissFailure)}>
                  <i className={`icon-aytmfd-x-v2 ${style['explore-popupClose']}`} />
                  <i className={`icon-aytmfd-skipper ${style['explore-popupIcon']}`} />
                  <b>
                    Sorry, mate, I&apos;ve lost my bearings...&nbsp;&nbsp;&nbsp;
                    <span className={style['explore-popupCompass']}>🧭</span>
                  </b>
                  <br />
                  <small>Let&apos;s give it another go in a bit.</small>
                </div>
              )}
            >
              {renderExploreButton()}
            </Popup>
          ) : (
            renderExploreButton()
          )}
        </div>
      ) : null}
    </div>
  )
}

QuestionCrosstab.propTypes = {
  popupDirection: PropTypes.string,
  // the head owns whether the last request failed; the bar only reports it and offers the dismissal
  requestFailed: PropTypes.bool,
  isThinking: PropTypes.bool,
  getCrosstabData: PropTypes.func,
  // the question head owns the request; the bar only offers the button that asks for it
  onExplore: PropTypes.func,
  onCrosstabChange: PropTypes.func,
  onDismissFailure: PropTypes.func
}

QuestionCrosstab.displayName = 'QuestionCrosstab'

export default QuestionCrosstab
